/*
 * Infinite "vitrine" for collection pages.
 *
 * Progressive enhancement over Dawn's numbered pagination: when JS is on we
 * load the remaining collection pages on scroll and keep every sold-out product
 * after every available one across the WHOLE collection (not just per page).
 *
 * SEO is preserved because:
 *   - The server still renders the normal page with numbered pagination.
 *   - Those numbered pagination links stay in the DOM (only visually hidden),
 *     so crawlers and no-JS visitors keep a fully paginated, crawlable view.
 *   - We only take over on page 1 of a result set; ?page=N deep links keep the
 *     native experience.
 *
 * It works on automated (smart) collections of any size because the available
 * vs sold-out split is done client-side from each card's data-product-available
 * attribute — no manual collection reordering and no app required.
 *
 * Targets Dawn's collection grid markup (#ProductGridContainer / #product-grid /
 * .pagination-wrapper). If those markers are missing it fails safe and leaves the
 * native paginated view untouched.
 */
(function () {
  'use strict';

  if (window.__infiniteVitrineLoaded) return;
  window.__infiniteVitrineLoaded = true;

  var GRID = '#product-grid';
  var CONTAINER = '#ProductGridContainer';
  // Cap auto-loads between user scrolls so we fill the viewport without
  // front-loading an entire (possibly huge) collection.
  var MAX_AUTO_LOADS = 4;

  // Strip Dawn's scroll-reveal classes from dynamically inserted cards. Those
  // cards start at opacity:0 and would never be revealed (the reveal observer
  // doesn't pick up nodes added after load). Also force their images to lazy:
  // injected cards are below the fold by definition.
  function makeVisible(li) {
    li.classList.remove(
      'scroll-trigger',
      'animate--slide-in',
      'animate--fade-in',
      'animate--zoom-in',
      'scroll-trigger--offscreen'
    );
    li.removeAttribute('data-cascade');
    if (li.style) li.style.removeProperty('--animation-order');
    var eager = li.querySelectorAll('img[loading="eager"]');
    for (var i = 0; i < eager.length; i++) eager[i].setAttribute('loading', 'lazy');
    return li;
  }

  function Vitrine(grid) {
    this.grid = grid;
    this.container = null;
    this.sectionId = grid.getAttribute('data-id');
    this.totalPages = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
    this.currentPage = parseInt(grid.getAttribute('data-current-page'), 10) || 1;
    this.nextPage = 2;
    this.loading = false;
    this.done = false;
    this.flushed = false;
    this.errored = false;
    this.autoLoads = 0;
    this.soldOut = [];
    this.seen = Object.create(null);
    this.observer = null;
    this.ui = null;
    this.spinner = null;
    this.moreBtn = null;
  }

  Vitrine.prototype.start = function () {
    // Only take over the first page of a (possibly filtered) result set. Deep
    // links to ?page=N keep the native numbered pagination that crawlers follow.
    if (this.currentPage !== 1) return;

    if (this.totalPages <= 1) {
      // Single page already in the DOM: just move sold-out to the very end.
      this.splitInitial();
      this.flush();
      return;
    }

    // We need the load-more UI to run infinite scroll safely. If it's missing
    // (template variant, Dawn change), leave the server-rendered grid AND the
    // numbered pagination untouched — do not strip sold-out cards.
    this.container = this.grid.closest('.collection') || this.grid.parentNode;
    var ui = this.container.querySelector('[data-infinite-vitrine]');
    if (!ui) return;
    this.ui = ui;

    this.splitInitial();
    this.setupUI();
    this.observe();
  };

  // Move sold-out cards already on page 1 into the buffer so the initial view
  // shows only available products first.
  Vitrine.prototype.splitInitial = function () {
    var items = Array.prototype.slice.call(this.grid.children);
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      var id = li.getAttribute('data-product-id');
      if (id) this.seen[id] = true;
      if (li.getAttribute('data-product-available') === 'false') {
        this.soldOut.push(this.grid.removeChild(li));
      }
    }
  };

  Vitrine.prototype.setupUI = function () {
    // Keep the numbered pagination in the DOM for SEO / no-JS, but hide it.
    var pagination = this.container.querySelector('.pagination-wrapper');
    if (pagination) pagination.style.display = 'none';

    this.ui.hidden = false;
    this.spinner = this.ui.querySelector('.infinite-vitrine__spinner');
    this.moreBtn = this.ui.querySelector('[data-infinite-vitrine-more]');
    if (this.spinner) this.spinner.style.display = 'none';

    var self = this;
    if (this.moreBtn) {
      this.moreBtn.addEventListener('click', function () {
        // A manual action resets the auto-load budget and clears any error so
        // the same (previously failed) page can be retried.
        self.autoLoads = 0;
        self.errored = false;
        self.loadNext();
      });
    }
  };

  Vitrine.prototype.observe = function () {
    if (!('IntersectionObserver' in window)) return;
    var self = this;
    this.observer = new IntersectionObserver(function (entries) {
      if (entries[0] && entries[0].isIntersecting) {
        // A real scroll into view resets the auto-load budget and lets a
        // previously failed page retry.
        self.autoLoads = 0;
        self.errored = false;
        self.loadNext();
      }
    }, { rootMargin: '800px 0px' });
    this.observer.observe(this.ui);
  };

  Vitrine.prototype.uiNearViewport = function () {
    if (!this.ui || this.ui.hidden) return false;
    var rect = this.ui.getBoundingClientRect();
    return rect.top <= (window.innerHeight || document.documentElement.clientHeight) + 800;
  };

  Vitrine.prototype.buildUrl = function (page) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.set('page', page);
    params.set('section_id', this.sectionId);
    return window.location.pathname + '?' + params.toString();
  };

  Vitrine.prototype.loadNext = function () {
    if (this.loading || this.done) return;
    if (this.nextPage > this.totalPages) { this.flush(); return; }

    this.loading = true;
    this.errored = false;
    if (this.spinner) this.spinner.style.display = '';
    if (this.moreBtn) this.moreBtn.disabled = true;

    var self = this;
    var page = this.nextPage;

    fetch(this.buildUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector(GRID);
        // A missing grid means a soft error / unexpected response. Treat it as a
        // failure (caught below) instead of progress, so we never advance past
        // real products or flush the sold-out buffer early.
        if (!newGrid) throw new Error('No product grid in response');
        if (self.done) return; // a re-init (filter change) superseded this load

        var items = Array.prototype.slice.call(newGrid.children);
        for (var i = 0; i < items.length; i++) {
          var li = items[i];
          var id = li.getAttribute('data-product-id');
          if (id && self.seen[id]) continue;
          if (id) self.seen[id] = true;
          makeVisible(li);
          if (li.getAttribute('data-product-available') === 'false') {
            self.soldOut.push(li);
          } else {
            self.grid.appendChild(li);
          }
        }
        self.nextPage = page + 1;
      })
      .catch(function () {
        // Leave nextPage unchanged and flag the error so the auto-continue loop
        // stops (no request storm). A real scroll or the button retries.
        self.errored = true;
      })
      .then(function () {
        self.loading = false;
        if (self.spinner) self.spinner.style.display = 'none';
        if (self.moreBtn) self.moreBtn.disabled = false;
        if (self.done) return;
        if (self.nextPage > self.totalPages) {
          self.flush();
        } else if (!self.errored && self.autoLoads < MAX_AUTO_LOADS && self.uiNearViewport()) {
          // Content didn't fill the viewport yet: keep going, but bounded.
          self.autoLoads++;
          window.requestAnimationFrame(function () { self.loadNext(); });
        }
      });
  };

  // All pages loaded: append the buffered sold-out products at the very end.
  Vitrine.prototype.flush = function () {
    if (this.flushed) return;
    this.flushed = true;
    this.done = true;

    if (this.soldOut.length) {
      var frag = document.createDocumentFragment();
      for (var i = 0; i < this.soldOut.length; i++) {
        frag.appendChild(makeVisible(this.soldOut[i]));
      }
      this.grid.appendChild(frag);
    }
    this.soldOut = [];

    if (this.observer) this.observer.disconnect();
    if (this.ui) this.ui.hidden = true;
  };

  // Cancel a superseded instance: stops its in-flight fetch from appending into
  // a detached grid and detaches its observer.
  Vitrine.prototype.destroy = function () {
    this.done = true;
    if (this.observer) this.observer.disconnect();
  };

  var current = null;

  function init() {
    var grid = document.querySelector(GRID);
    if (!grid) return;
    if (grid.getAttribute('data-infinite-scroll') !== 'true') return;
    if (current && grid === current.grid) return; // already initialized this grid
    if (current) current.destroy();
    current = new Vitrine(grid);
    current.start();
  }

  var containerObserver = null;
  function watch() {
    var container = document.querySelector(CONTAINER);
    if (!container) return;
    if (containerObserver) containerObserver.disconnect();
    // Dawn's facets replace #ProductGridContainer's innerHTML on filter/sort
    // changes. Watch only direct children so our own deep appends don't refire.
    containerObserver = new MutationObserver(function () { init(); });
    containerObserver.observe(container, { childList: true });
  }

  function boot() { init(); watch(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-bind when the section is re-rendered so merchants can
  // preview the vitrine while editing.
  document.addEventListener('shopify:section:load', boot);
})();
